'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import { Plus, Pencil, Trash2, Loader2, Users, Shield } from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { MetricsSkeleton } from '@/components/metrics-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SelectAllBanner } from '@/components/select-all-banner';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import { ROLES_HASH } from '../constants';
import { roleQueries } from '../services/roles.queries';
import {
  useDeleteRoleMutation,
  useDuplicateRoleMutation
} from '../services/roles.mutations';
import { RoleColorBadge } from './role-color-badge';
import { RoleCard } from './role-card';
import { RoleCompactRow } from './role-compact-row';
import { RoleFormDialog } from './role-form-dialog';
import { AssignRoleDialog } from './assign-role-dialog';
import { RolesTableActionBar } from './roles-table-action-bar';
import { RolePermissionsPanel } from './role-permissions-panel';
import { PermissionBadgeList } from './permission-badge-list';
import {
  isColumnPermission,
  isHiddenPermission
} from './role-permissions-matrix-config';
import { useHiddenPermissionGroups } from './use-hidden-permission-groups';
import { getRoleDisplayName } from '../mappers/role.mapper';
import type { Role, RolesListParams } from '../types';

export function RoleListing() {
  const t = useTranslations('roles');
  const locale = useLocale();
  // Overlay open-state lives in the URL hash (#create, #edit/<id>,
  // #delete/<id>, #assign/<id>, #permissions/<id>) so overlays are
  // deep-linkable and close on browser Back. Destructure the
  // stable callbacks so they can go in hook dep arrays without tripping the
  // React Compiler's manual-memoization check.
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    ROLES_HASH.create
  );
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(ROLES_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(ROLES_HASH.delete);
  const {
    open: assignOpen,
    param: assignId,
    openWith: openAssign,
    onOpenChange: closeAssign
  } = useHashParamDialog(ROLES_HASH.assign);
  const {
    open: permsPanelOpen,
    param: permsPanelId,
    openWith: openPermsPanel,
    onOpenChange: closePermsPanel
  } = useHashParamDialog(ROLES_HASH.permissions);
  const [view, setView] = useDataView('roles');
  const hiddenGroups = useHiddenPermissionGroups();

  const columns: ColumnDef<Role>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('roleDisplayName')} />
      ),
      cell: ({ row, column }) => (
        <RoleColorBadge
          variant='table'
          name={
            <HighlightMatch
              text={getRoleDisplayName(row.original, locale)}
              query={getColumnSearchTerms(column)}
            />
          }
          color={row.original.color}
          onClick={() => openPermsPanel(String(row.original.id))}
        />
      ),
      enableColumnFilter: true,
      meta: {
        label: t('searchPlaceholder'),
        variant: 'text' as const,
        placeholder: t('searchPlaceholder'),
        permission: 'roles.column.roleDisplayName'
      }
    },
    {
      id: 'users_count',
      accessorKey: 'users_count',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('usersCount')} />
      ),
      cell: ({ row }) => (
        <div className='flex items-center gap-1.5'>
          <Users className='text-muted-foreground h-4 w-4' />
          <span>{row.original.users_count}</span>
        </div>
      ),
      enableColumnFilter: true,
      meta: {
        label: t('usersCount'),
        variant: 'multiSelect' as const,
        icon: 'solar:users-group-rounded-bold-duotone',
        options: [
          { label: t('hasUsers'), value: 'yes' },
          { label: t('noUsers'), value: 'no' }
        ],
        permission: 'roles.column.usersCount'
      }
    },
    {
      id: 'permissions',
      accessorFn: (row) => row.permissions.length,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('permissions')} />
      ),
      cell: ({ row }) => (
        <PermissionBadgeList
          permissions={row.original.permissions}
          filter={(name) =>
            !name.endsWith('.view_filter') &&
            !isColumnPermission(name) &&
            !isHiddenPermission(name, hiddenGroups)
          }
          emptyFallback={
            <span className='text-muted-foreground text-xs'>—</span>
          }
        />
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: { permission: 'roles.column.permissions' }
    },
    {
      id: 'viewFilters',
      accessorFn: (row) =>
        row.permissions.filter(
          (p) =>
            p.endsWith('.view_filter') && !isHiddenPermission(p, hiddenGroups)
        ).length,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('viewFilters')} />
      ),
      cell: ({ row }) => (
        <PermissionBadgeList
          permissions={row.original.permissions}
          filter={(name) =>
            name.endsWith('.view_filter') &&
            !isHiddenPermission(name, hiddenGroups)
          }
          emptyFallback={
            <span className='text-muted-foreground text-xs'>—</span>
          }
        />
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: { permission: 'roles.column.viewFilters' }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const role = row.original;
        return (
          <div className='flex items-center justify-end gap-1'>
            <PermissionGate permissions={['roles.edit']}>
              <Button
                variant='outline'
                size='sm'
                onClick={() => openPermsPanel(String(role.id))}
              >
                <Shield className='me-1.5 h-3.5 w-3.5' />
                {t('permissions')}
              </Button>
              {role.can_edit && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => openEdit(String(role.id))}
                >
                  <Pencil className='me-1.5 h-3.5 w-3.5' />
                  {t('edit')}
                </Button>
              )}
            </PermissionGate>
            {role.can_delete && (
              <PermissionGate permissions={['roles.delete']}>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-8 w-8'
                  onClick={() => openDelete(String(role.id))}
                >
                  <Trash2 className='text-destructive h-4 w-4' />
                </Button>
              </PermissionGate>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState('name', parseAsString.withDefault(''));
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Role>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Role>[])
  );
  const [urlPermissions] = useQueryState(
    'permissions_count',
    parseAsString.withDefault('')
  );
  const [urlUsersCount] = useQueryState(
    'users_count',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );

  const queryParams = useMemo<RolesListParams>(() => {
    const params: RolesListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    params.sort_by =
      (urlSorting[0]?.id as RolesListParams['sort_by']) ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    if (urlPermissions) {
      const raw = String(urlPermissions);
      const parts = raw
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_permissions = parts[0];
      if (parts.length >= 2) params.max_permissions = parts[1];
    }

    if (urlUsersCount.length) {
      const values = urlUsersCount.filter(
        (v): v is 'yes' | 'no' => v === 'yes' || v === 'no'
      );
      if (values.length) {
        params.has_users = values;
      }
    }

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlSorting,
    urlPermissions,
    urlUsersCount
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    roleQueries.list(queryParams)
  );
  const { data: metricsData, isPending: metricsPending } = useQuery(
    roleQueries.metrics()
  );
  const deleteMutation = useDeleteRoleMutation();
  const duplicateMutation = useDuplicateRoleMutation();

  const roles = useMemo(() => (data?.data ?? []) as Role[], [data?.data]);
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id params back to loaded rows (row must be on the current
  // page; deep-linking an off-page id simply won't open the overlay).
  const selectedRole = useMemo(
    () =>
      permsPanelOpen
        ? (roles.find((r) => String(r.id) === permsPanelId) ?? null)
        : null,
    [permsPanelOpen, permsPanelId, roles]
  );
  const editTarget = editOpen
    ? (roles.find((r) => String(r.id) === editId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (roles.find((r) => String(r.id) === deleteId) ?? null)
    : null;
  const assignTarget = assignOpen
    ? (roles.find((r) => String(r.id) === assignId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Role>({
    columns: filteredColumns,
    pageKey: 'roles',
    data: roles,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const tableState = table.state;

  const handleEdit = useCallback(
    (role: Role) => openEdit(String(role.id)),
    [openEdit]
  );

  const handleAssign = useCallback(
    (role: Role) => openAssign(String(role.id)),
    [openAssign]
  );

  const handleDelete = useCallback(
    (role: Role) => openDelete(String(role.id)),
    [openDelete]
  );

  const handleSelectRole = useCallback(
    (role: Role) => openPermsPanel(String(role.id)),
    [openPermsPanel]
  );

  async function handleDuplicate(role: Role) {
    try {
      await duplicateMutation.mutateAsync(role.id);
    } catch {
      // handled by global MutationCache
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const force = deleteTarget.users_count > 0;
    try {
      await deleteMutation.mutateAsync({ id: deleteTarget.id, force });
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  const sortOptions = useMemo(
    () => [
      { label: t('roleDisplayName'), value: 'name' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  const isForceDelete = deleteTarget && deleteTarget.users_count > 0;

  return (
    <div className='space-y-6'>
      {/* Metrics */}
      {metricsPending ? (
        <MetricsSkeleton />
      ) : metricsData?.data ? (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          <Card>
            <CardContent className='flex flex-col items-center justify-center pt-6'>
              <p className='text-4xl font-bold text-emerald-500'>
                {metricsData.data.total_roles}
              </p>
              <p className='text-muted-foreground mt-1 text-sm'>
                {t('totalRoles')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex flex-col items-center justify-center pt-6'>
              <p className='text-4xl font-bold text-blue-500'>
                {metricsData.data.total_users_with_roles}
              </p>
              <p className='text-muted-foreground mt-1 text-sm'>
                {t('usersWithRoles')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex flex-col items-center justify-center pt-6'>
              <p className='text-4xl font-bold text-amber-500'>
                {metricsData.data.avg_permissions_per_role}
              </p>
              <p className='text-muted-foreground mt-1 text-sm'>
                {t('avgPermissions')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex flex-col items-center justify-center pt-6'>
              <p className='text-4xl font-bold text-rose-500'>
                {metricsData.data.total_permissions}
              </p>
              <p className='text-muted-foreground mt-1 text-sm'>
                {t('permissionTypes')}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Header controls */}
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='roles'
          />
        </div>
        <PermissionGate permissions={['roles.create']}>
          <Button onClick={() => toggleCreate(true)}>
            <Plus className='me-2 h-4 w-4' />
            {t('newRole')}
          </Button>
        </PermissionGate>
      </div>

      {/* Data views */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : roles.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Users className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noRoles')}</p>
        </div>
      ) : (
        <>
          {/* Permissions panel — slides in from the right (#permissions/<id>) */}
          <RolePermissionsPanel
            role={selectedRole}
            locale={locale}
            onClose={() => closePermsPanel(false)}
          />

          {/* Content area */}
          <div className='min-w-0 flex-1'>
            <DataTableToolbar
              table={table}
              viewFilterPermission='roles.view_filter'
            />
            {view === 'grid' ? (
              <>
                <div
                  className={cn(
                    'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
                    isPlaceholderData && 'opacity-60 transition-opacity'
                  )}
                >
                  {roles.map((role) => (
                    <RoleCard
                      key={role.id}
                      role={role}
                      onAssign={handleAssign}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                      onSelectRole={handleSelectRole}
                      isDuplicating={duplicateMutation.isPending}
                    />
                  ))}
                </div>
                {pagination && pagination.totalPages > 0 && (
                  <PaginationControls
                    page={pagination.page}
                    pageCount={pagination.totalPages}
                    perPage={pagination.per_page}
                    total={pagination.total}
                    onPageChange={(p) => table.setPageIndex(p - 1)}
                    onPerPageChange={(s) => {
                      table.setPageSize(s);
                      table.setPageIndex(0);
                    }}
                  />
                )}
              </>
            ) : view === 'compact' ? (
              <>
                <div
                  className={cn(
                    'space-y-2',
                    isPlaceholderData && 'opacity-60 transition-opacity'
                  )}
                >
                  {roles.map((role) => (
                    <RoleCompactRow
                      key={role.id}
                      role={role}
                      onAssign={handleAssign}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                      onSelectRole={handleSelectRole}
                      isDuplicating={duplicateMutation.isPending}
                    />
                  ))}
                </div>
                {pagination && pagination.totalPages > 0 && (
                  <PaginationControls
                    page={pagination.page}
                    pageCount={pagination.totalPages}
                    perPage={pagination.per_page}
                    total={pagination.total}
                    onPageChange={(p) => table.setPageIndex(p - 1)}
                    onPerPageChange={(s) => {
                      table.setPageSize(s);
                      table.setPageIndex(0);
                    }}
                  />
                )}
              </>
            ) : (
              <div
                className={cn(
                  'flex min-h-[500px] min-w-0 flex-1 flex-col gap-2',
                  isPlaceholderData && 'opacity-60 transition-opacity'
                )}
              >
                <SelectAllBanner
                  isAllPagesSelected={selectAll.isAllPagesSelected}
                  isCurrentPageFullySelected={
                    selectAll.isCurrentPageFullySelected
                  }
                  currentPageCount={roles.length}
                  totalCount={pagination?.total ?? 0}
                  pageSize={tableState.pagination.pageSize}
                  onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
                  onClearSelection={() => selectAll.reset()}
                />
                <DataTable
                  table={table}
                  actionBar={
                    <RolesTableActionBar
                      table={table}
                      getBulkPayload={getBulkPayload}
                      onComplete={handleBulkComplete}
                      isAllPagesSelected={selectAll.isAllPagesSelected}
                      totalCount={pagination?.total ?? 0}
                    />
                  }
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Assign role dialog — #assign/<id> */}
      <AssignRoleDialog
        open={assignOpen && !!assignTarget}
        onOpenChange={closeAssign}
        role={assignTarget}
      />

      {/* Form dialog — #create / #edit/<id> */}
      <RoleFormDialog
        open={createOpen || (editOpen && !!editTarget)}
        onOpenChange={(open) => {
          if (!open) (editOpen ? closeEdit : toggleCreate)(false);
        }}
        role={editOpen ? editTarget : null}
        onCreated={(roleId) => openPermsPanel(String(roleId))}
      />

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isForceDelete ? (
                <span className='text-destructive font-medium'>
                  {t('forceDeleteWarning', {
                    count: deleteTarget?.users_count ?? 0
                  })}
                </span>
              ) : (
                t('deleteConfirmDescription', {
                  name: deleteTarget?.name ?? ''
                })
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
